import { useEffect, useRef, useState } from "react";

import { translateShared } from "../i18n/sharedCatalog";
import { BUILT_IN_SOURCE_LOCALE } from "../i18n/preparedLocales";
import type { FeedbackState } from "./feedback";
import { FeedbackDialog } from "./layers";

const INLINE_VALIDATION_SELECTOR = [
  ".kavoosh-field__error",
  "[data-kavoosh-inline-validation='true']",
  "[aria-describedby$='-error']",
].join(",");

function candidateFrom(element: HTMLElement): FeedbackState | null {
  if (element.closest(".kavoosh-layer-backdrop")) return null;
  if (element.matches(INLINE_VALIDATION_SELECTOR) || element.closest(".kavoosh-field")) {
    return null;
  }
  const message = element.textContent?.replace(/\s+/g, " ").trim() ?? "";
  if (!message) return null;
  const warning = /warning|source-warning|is-warning|caution/i.test(element.className);
  return {
    kind: warning ? "partial" : "error",
    title: translateShared(
      BUILT_IN_SOURCE_LOCALE,
      warning ? "feedback.warningTitle" : "feedback.errorTitle",
    ),
    message,
    titleKey: warning ? "feedback.warningTitle" : "feedback.errorTitle",
    tone: warning ? "warning" : "danger",
    announced: true,
  };
}

function signature(state: FeedbackState): string {
  return `${state.tone}\u0000${state.message}`;
}

export function ProminentFeedbackRuntime() {
  const [current, setCurrent] = useState<FeedbackState | null>(null);
  const dismissed = useRef(new Set<string>());
  const activeSignature = useRef<string | null>(null);

  useEffect(() => {
    let scheduled = false;
    const scan = () => {
      scheduled = false;
      const candidates = Array.from(
        document.querySelectorAll<HTMLElement>("[role='alert']"),
      );
      for (const element of candidates) {
        const state = candidateFrom(element);
        if (!state) continue;
        const nextSignature = signature(state);
        if (
          dismissed.current.has(nextSignature)
          || activeSignature.current === nextSignature
        ) {
          continue;
        }
        activeSignature.current = nextSignature;
        setCurrent(state);
        return;
      }
    };
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(scan);
    };
    scan();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["role", "class", "hidden", "aria-hidden"],
    });
    return () => observer.disconnect();
  }, []);

  return <FeedbackDialog
    state={current}
    onDismiss={() => {
      if (activeSignature.current) dismissed.current.add(activeSignature.current);
      activeSignature.current = null;
      setCurrent(null);
    }}
  />;
}
